import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
// ErrorService is imported from harmony package (UI layer) because it handles user-facing
// error display via snackbar. This is intentional: conductor (HTTP/API layer) delegates
// UI concerns to harmony, maintaining separation of concerns.
import { ErrorService } from '@complexcare/harmony';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { AuthStateService } from '../../auth/auth-core/services/auth-state.service';
import { AuthErrorContextService, ErrorContextType } from '../../auth/services/error-context.service';
import { ShellAuthLifecycleService } from '../../auth/shell-orchestrator/services/shell-auth-lifecycle.service';
import { FHIR_API_BASE_URL } from '../../http/api-client.tokens';
import { ActivityTrackerService } from '../../services/activity-tracker.service';
import { AppHttpError } from '../models/app-http-error.model';
import { getAuthTokenForRequest } from './auth.interceptor';
import { hasLogoutIntent } from '../../auth/session-sync/utils/session-storage.util';
import { CORRELATION_ID_HEADER } from './header-injection.context';

/** Context token to track if request was already retried after token refresh. */
const RETRIED_AFTER_REFRESH = new HttpContextToken<boolean>(() => false);

/**
 * HTTP Context Token for custom error messages Allows API call initiators to provide custom error messages via
 * HttpContext
 *
 * @example
 *   ```typescript
 *   this.http.get('/api/data', {
 *     context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Failed to load data')
 *   });
 *   ```;
 */
export const CUSTOM_ERROR_MESSAGE = new HttpContextToken<string>(() => '');

/**
 * HTTP Context Token to suppress error notifications When set to true, the interceptor will not show a snackbar for
 * this request
 *
 * NOTE: when a request crosses a Module Federation remote->shell boundary (e.g. `web.assessment` hosted inside
 * `web.shell` via `withRequestsMadeViaParent()`), the shell resolves its own, separately bundled copy of this token, so
 * a value set against the remote's instance is invisible there. Pair this with the `SUPPRESS_ERROR_NOTIFICATION_HEADER`
 * below, which survives that boundary.
 *
 * @example
 *   ```typescript
 *   this.http.get('/api/data', {
 *     context: new HttpContext().set(SUPPRESS_ERROR_NOTIFICATION, true)
 *   });
 *   ```;
 */
export const SUPPRESS_ERROR_NOTIFICATION = new HttpContextToken<boolean>(() => false);

/**
 * HTTP header recognized by `errorInterceptor` as an alternative to `SUPPRESS_ERROR_NOTIFICATION`. Preferred whenever a
 * request may cross a Module Federation remote/host boundary, since header values are plain strings and survive that
 * boundary (unlike a reference-equality HttpContextToken).
 */
export const SUPPRESS_ERROR_NOTIFICATION_HEADER = 'X-Suppress-Error-Notification';

interface ErrorPayload {
  code?: string;
  errorCode?: string;
  message?: string;
  error?: string;
  title?: string;
  detail?: string;
  status?: number;
  details?: unknown;
  traceId?: string;
  correlationId?: string;
}

/** Returns true when a message is a low-level transport/runtime error that should not be shown directly to end users. */
function isTechnicalNetworkMessage(message: string): boolean {
  const normalized = message.trim().toLowerCase();

  return (
    normalized === 'failed to fetch' ||
    normalized.includes('networkerror when attempting to fetch resource') ||
    normalized.includes('load failed') ||
    normalized.includes('err_connection_refused') ||
    normalized.includes('err_connection_reset') ||
    normalized.includes('err_connection_timed_out') ||
    normalized.includes('err_name_not_resolved')
  );
}

/**
 * Parses error response body if it's a Blob Angular HttpClient sometimes returns error bodies as Blobs instead of
 * parsed JSON
 */
async function parseErrorBody(errorBody: unknown): Promise<ErrorPayload | undefined> {
  if (!errorBody) {
    return undefined;
  }

  // If it's already a parsed object, return it
  if (typeof errorBody === 'object' && !(errorBody instanceof Blob)) {
    return errorBody as ErrorPayload;
  }

  // If it's a Blob, read and parse it
  if (errorBody instanceof Blob) {
    try {
      const text = await errorBody.text();
      if (text) {
        return JSON.parse(text) as ErrorPayload;
      }
    } catch (e) {
      console.error('Failed to parse error Blob:', e);
    }
  }

  return undefined;
}

/**
 * Maps HttpErrorResponse to standardized AppHttpError model Handles ASP.NET Core ProblemDetails format and fallback
 * scenarios
 */
async function mapHttpError(error: HttpErrorResponse, request?: HttpRequest<unknown>): Promise<AppHttpError> {
  const payload = await parseErrorBody(error.error);
  const rawStringMessage = typeof error.error === 'string' && error.error.trim() ? error.error.trim() : undefined;

  const headerCorrelationId =
    error.headers?.get(CORRELATION_ID_HEADER) ||
    error.headers?.get('x-correlation-id') ||
    request?.headers?.get(CORRELATION_ID_HEADER) ||
    request?.headers?.get('x-correlation-id') ||
    undefined;

  const resolvedCorrelationId =
    payload?.correlationId ??
    ((payload as Record<string, unknown> | undefined)?.['CorrelationId'] as string) ??
    payload?.traceId ??
    headerCorrelationId ??
    undefined;

  return {
    status: payload?.status ?? error.status,
    code: payload?.code ?? payload?.errorCode ?? `HTTP_${(payload?.status ?? error.status) || 0}`,
    message:
      payload?.message ??
      payload?.error ??
      payload?.detail ??
      payload?.title ??
      rawStringMessage ??
      error.message ??
      'An unexpected server error occurred.',
    details: payload?.details ?? payload ?? error.error,
    traceId: resolvedCorrelationId,
    correlationId: resolvedCorrelationId,
  };
}

/** Get user-friendly error message based on HTTP status code */
function getUserFriendlyMessage(error: AppHttpError, customMessage?: string): string {
  // Use custom message if provided
  if (customMessage) {
    return customMessage;
  }

  // Use server-provided message if available and informative
  if (
    error.message &&
    error.message !== 'An unexpected server error occurred.' &&
    !error.message.includes('Http failure') &&
    !isTechnicalNetworkMessage(error.message)
  ) {
    return error.message;
  }

  // Default messages based on status code
  switch (error.status) {
    case 400:
      return 'Invalid request. Please check your input and try again.';
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'The requested resource was not found.';
    case 409:
      return 'This action conflicts with existing data.';
    case 422:
      return 'The submitted data could not be processed.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'A server error occurred. Please try again.';
    case 502:
      return 'The server is temporarily unavailable. Please try again.';
    case 503:
      return 'The service is temporarily unavailable. Please try again later.';
    case 504:
      return 'The request timed out. Please try again.';
    default:
      return error.status >= 500
        ? 'A server error occurred. Please try again.'
        : 'An error occurred. Please try again.';
  }
}

/**
 * Categorizes auth/access errors to provide contextual error handling Used to determine which error type to display and
 * what recovery actions to offer
 */
function categorizeAuthError(error: AppHttpError): ErrorContextType {
  if (error.status !== 401 && error.status !== 403) {
    return 'auth-failure';
  }

  const messageLower = (error.message ?? '').toLowerCase();

  // Detect account-not-found errors
  if (
    messageLower.includes('account not found') ||
    messageLower.includes('user not found') ||
    messageLower.includes('no account') ||
    messageLower.includes('account does not exist')
  ) {
    return 'account-not-found';
  }

  // Detect token-expired errors
  if (
    messageLower.includes('token expired') ||
    messageLower.includes('session expired') ||
    messageLower.includes('token is no longer valid') ||
    messageLower.includes('session has expired')
  ) {
    return 'token-expired';
  }

  // 403 = access-denied
  if (error.status === 403) {
    return 'access-denied';
  }

  // Default 401 handling
  return 'auth-failure';
}

/**
 * Error Interceptor
 *
 * - Catches all HTTP errors in one place
 * - Maps errors to standardized AppHttpError format
 * - Displays user-friendly error messages via ErrorService
 * - Supports custom error messages via HttpContext
 * - Logs errors for monitoring
 * - Handles ASP.NET Core ProblemDetails format
 *
 * Features:
 *
 * - Custom error messages via CUSTOM_ERROR_MESSAGE context token
 * - Suppress notifications via SUPPRESS_ERROR_NOTIFICATION context token
 * - Automatic status code-based messaging
 * - Silent handling for 401 (handled by auth flow)
 *
 * @example
 *   ```typescript
 *   // Basic usage - uses default error messages
 *   this.http.get('/api/data').subscribe();
 *
 *   // With custom error message
 *   this.http.get('/api/data', {
 *     context: new HttpContext().set(CUSTOM_ERROR_MESSAGE, 'Failed to load your data')
 *   }).subscribe();
 *
 *   // Suppress notification (handle errors locally)
 *   this.http.get('/api/data', {
 *     context: new HttpContext().set(SUPPRESS_ERROR_NOTIFICATION, true)
 *   }).subscribe({
 *     error: (err) => this.handleLocally(err)
 *   });
 *   ```;
 */
export const errorInterceptor: HttpInterceptorFn = (request, next) => {
  const errorService = inject(ErrorService);
  const activityTracker = inject(ActivityTrackerService);
  const authLifecycle = inject(ShellAuthLifecycleService);
  const authStateService = inject(AuthStateService);
  const fhirApiBaseUrl = inject(FHIR_API_BASE_URL, { optional: true });
  const errorContextService = inject(AuthErrorContextService);

  return next(request).pipe(
    catchError((error: unknown) => {
      // Get custom message and suppression flag from request context
      const customMessage = request.context.get(CUSTOM_ERROR_MESSAGE);
      const suppressNotification =
        request.context.get(SUPPRESS_ERROR_NOTIFICATION) ||
        request.headers.get(SUPPRESS_ERROR_NOTIFICATION_HEADER) === 'true' ||
        request.url.includes('/api/Weather/current-conditions');

      if (!(error instanceof HttpErrorResponse)) {
        const unknownError: AppHttpError = {
          status: 0,
          code: 'UNKNOWN_ERROR',
          message: 'An unexpected error occurred.',
          details: error,
        };

        // Log error
        errorService.logError(unknownError, {
          url: request.urlWithParams,
          method: request.method,
        });

        // Record as a Failed event when a simulation session is active
        if (activityTracker.sessionType === 'simulation') {
          activityTracker.recordEvent({
            stepName: 'API Error',
            stepDescription: `${request.method} ${request.urlWithParams} — unexpected error`,
            requestPayload: JSON.stringify({ url: request.urlWithParams, method: request.method }),
            status: 'Failed',
            errorMessage: 'An unexpected error occurred.',
          });
        }

        // Show error notification unless suppressed
        if (!suppressNotification) {
          const message = customMessage || 'An unexpected error occurred. Please try again.';
          errorService.showError(message);
        }

        return throwError(() => unknownError);
      }

      // Map the HTTP error (async operation to handle Blob responses)
      return from(mapHttpError(error, request)).pipe(
        switchMap((mappedError) => {
          // Log to console and external monitoring
          errorService.logError(mappedError, {
            url: request.urlWithParams,
            method: request.method,
          });

          // Record as a Failed event when a simulation session is active
          if (activityTracker.sessionType === 'simulation' && mappedError.status !== 401) {
            activityTracker.recordEvent({
              stepName: `API Error ${mappedError.status}`,
              stepDescription: `${request.method} ${request.urlWithParams} — ${mappedError.message}`,
              requestPayload: JSON.stringify({ url: request.urlWithParams, method: request.method }),
              status: 'Failed',
              errorMessage: `HTTP ${mappedError.status}: ${mappedError.message}`,
            });
          }

          // Handle 401: Attempt token refresh + retry once
          if (mappedError.status === 401) {
            const alreadyRetried = request.context.get(RETRIED_AFTER_REFRESH);

            if (!alreadyRetried) {
              // First 401: Attempt refresh and retry with new token
              return authLifecycle.refreshToken().pipe(
                switchMap(() => {
                  console.log('[errorInterceptor] Token refreshed successfully, retrying request with new token');

                  // Read fresh session after refresh completes
                  const session = authStateService.getSession();

                  // Use centralized token selection logic from auth.interceptor
                  const token = getAuthTokenForRequest(request.url, session, fhirApiBaseUrl);

                  // Build retry request with new token injected
                  let retryRequest = request.clone({
                    context: request.context.set(RETRIED_AFTER_REFRESH, true),
                  });

                  if (token) {
                    console.log('[errorInterceptor] Injecting new token (via getAuthTokenForRequest)');
                    retryRequest = retryRequest.clone({
                      setHeaders: { Authorization: `Bearer ${token}` },
                    });
                  }

                  return next(retryRequest);
                }),
                catchError((refreshError) => {
                  console.error('[errorInterceptor] Failed to refresh token:', refreshError);
                  // Refresh failed - redirect to unauthorized only if online
                  if (typeof window !== 'undefined') {
                    const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
                    const path = window.location.pathname;
                    const sessionStatus = authStateService.getSession().status;
                    const isLoggingOut =
                      sessionStatus === 'signing-out' || sessionStatus === 'signed-out' || hasLogoutIntent();
                    const publicPaths = ['/unauthorized', '/login', '/logout', '/health', '/offline', '/error'];
                    const isPublicPath = publicPaths.some((p) => path.startsWith(p));
                    if (!isPublicPath && !isOffline && !isLoggingOut) {
                      errorContextService.setContext({
                        type: 'auth-failure',
                        message: 'Authentication token refresh failed. Please sign in again.',
                        recoveryAction: 'retry',
                        timestamp: Date.now(),
                        correlationId: mappedError.correlationId ?? mappedError.traceId,
                      });
                      window.location.assign('/unauthorized');
                    }
                  }
                  return throwError(() => refreshError);
                })
              );
            } else {
              // Retry also failed - redirect to unauthorized only if online
              if (typeof window !== 'undefined') {
                const isOffline = typeof navigator !== 'undefined' && !navigator.onLine;
                const path = window.location.pathname;
                const sessionStatus = authStateService.getSession().status;
                const isLoggingOut =
                  sessionStatus === 'signing-out' || sessionStatus === 'signed-out' || hasLogoutIntent();
                const publicPaths = ['/unauthorized', '/login', '/logout', '/health', '/offline', '/error'];
                const isPublicPath = publicPaths.some((p) => path.startsWith(p));
                if (!isPublicPath && !isOffline && !isLoggingOut) {
                  errorContextService.setContext({
                    type: 'auth-failure',
                    message: 'Authentication failed. Please sign in again.',
                    recoveryAction: 'retry',
                    timestamp: Date.now(),
                    correlationId: mappedError.correlationId ?? mappedError.traceId,
                  });
                  window.location.assign('/unauthorized');
                }
              }
              return throwError(() => mappedError);
            }
          }

          // Handle auth/access errors (403) with contextual messaging and snackbar
          if (mappedError.status === 403 && !suppressNotification) {
            const errorContextType = categorizeAuthError(mappedError);
            const userMessage = getUserFriendlyMessage(mappedError, customMessage);

            // Show snackbar immediately so user sees error before redirect
            errorService.showError(userMessage);

            // Store context for unauthorized page to display specific error and recovery actions
            errorContextService.setContext({
              type: errorContextType,
              message: userMessage,
              recoveryAction: errorContextType === 'account-not-found' ? 'contact-support' : 'retry',
              timestamp: Date.now(),
              correlationId: mappedError.correlationId ?? mappedError.traceId,
            });
          }

          // For 403, show snackbar and let the flow continue (guards will handle redirection if needed)
          if (mappedError.status === 403 && !suppressNotification) {
            // Already shown snackbar above, just re-throw
          }

          // Handle other errors (not auth/access related)
          // Note: 409 is included here — callers that need custom conflict handling
          // (e.g. FHIR editor optimistic concurrency) must opt out via SUPPRESS_ERROR_NOTIFICATION.
          if (mappedError.status !== 401 && mappedError.status !== 403 && !suppressNotification) {
            const userMessage = getUserFriendlyMessage(mappedError, customMessage);
            errorService.showError(userMessage);
          }

          return throwError(() => mappedError);
        })
      );
    })
  );
};
